import { logger } from 'modules/logger';
import type { NvoqMessage } from '../createNvoqWebsocketFactory';
import { addCapitalizedAutoCorrectEntries } from './addCapitalizedAutoCorrectEntries';
import { SIRONA_SUBSTITUTION_REGEX, isLetter } from './utils';

const remapBoundaryAfterReplacement = (
  boundaryIndex: number,
  replacementStart: number,
  replacedLength: number,
  replacementLength: number
): number => {
  if (boundaryIndex <= replacementStart) {
    return boundaryIndex;
  }

  const replacementEnd = replacementStart + replacedLength;
  if (boundaryIndex >= replacementEnd) {
    return boundaryIndex + (replacementLength - replacedLength);
  }

  const offsetWithinReplacedText = boundaryIndex - replacementStart;
  return (
    replacementStart +
    Math.floor((offsetWithinReplacedText * replacementLength) / Math.max(replacedLength, 1))
  );
};

const runAutoCorrect = (
  autoCorrectedText: string,
  _dictionary: {
    [key: string]: string;
  },
  opts?: { boundaryIndex?: number }
): { text: string; boundaryIndex?: number } => {
  const srnaPositions: Array<{
    end: any;
    start: any;
  }> = [];

  const initialText = autoCorrectedText;
  let boundaryIndex = opts?.boundaryIndex;

  const correctedPhrases: Array<string> = [];

  // Extract <srna> </srna> tagged text and store their positions in srnaPositions
  autoCorrectedText = autoCorrectedText.replace(SIRONA_SUBSTITUTION_REGEX, (match, _, offset) => {
    srnaPositions.push({ start: offset, end: offset + match.length });
    return match;
  });

  const dictionary = addCapitalizedAutoCorrectEntries(_dictionary);

  // Longer autoCorrects are prioritized over shorter ones
  // Get dictionary keys sorted by length in descending order
  const sortedDictionary = Object.keys(dictionary).sort((a, b) => b.length - a.length);

  // Loop through the sorted dictionary and make replacements
  sortedDictionary.forEach((currentPhrase) => {
    const correctedWord = dictionary[currentPhrase];

    const autoCorrectedTextRef = autoCorrectedText;
    const correctedPhrasesRef = correctedPhrases;

    let startIndex = 0;
    while (true) {
      const index = autoCorrectedText.indexOf(currentPhrase, startIndex);
      if (index === -1) break;

      // only make replacement if both prefix and suffix boundary rules are met
      let prefixWordBoundaryRulesMet = false;
      let suffixWordBoundaryRulesMet = false;

      const doesCurrentPhraseBeginWithLetter = isLetter(currentPhrase[0]);

      if (!doesCurrentPhraseBeginWithLetter) {
        prefixWordBoundaryRulesMet = true;
      } else {
        const isStartOfText = index === 0;
        const isPrefixCharacterWhitespaceOrPunctuation = /[\s'"]/.test(
          autoCorrectedText[index - 1]
        );

        if (isStartOfText || isPrefixCharacterWhitespaceOrPunctuation) {
          prefixWordBoundaryRulesMet = true;
        }
      }

      const doesCurrentPhraseEndWithLetter = isLetter(currentPhrase[currentPhrase.length - 1]);

      if (!doesCurrentPhraseEndWithLetter) {
        suffixWordBoundaryRulesMet = true;
      } else {
        const isEndOfText = index + currentPhrase.length === autoCorrectedText.length;
        const isSuffixCharacterWhitespaceOrPunctuation = /[\s,.!?:'"]/.test(
          autoCorrectedText[index + currentPhrase.length]
        );

        if (isEndOfText || isSuffixCharacterWhitespaceOrPunctuation) {
          suffixWordBoundaryRulesMet = true;
        }
      }

      const boundaryRulesMet = prefixWordBoundaryRulesMet && suffixWordBoundaryRulesMet;

      // Only make replacement if the corrected word is not inside <srna></srna> tags
      const isInsideSrnaTag = srnaPositions.some(
        (srna) => srna.start <= index && srna.end >= index + currentPhrase.length
      );

      // Go through the autoCorrects that have already happened.
      // If we come across a shorter phrase in the autoCorrect dictionary that matches a phrase already autoCorrected
      // Ignore it, if its part of the autoCorrected phrase, otherwise correct it, if unrelated to an already autoCorrected phrase
      const matchesAlreadyAutoCorrectedCorrectedPhrase = correctedPhrasesRef.some(
        (correctedPhrase) =>
          correctedPhrase.includes(currentPhrase) &&
          autoCorrectedTextRef.substr(
            index - correctedPhrase.indexOf(currentPhrase),
            correctedPhrase.length
          ) === correctedPhrase
      );

      if (boundaryRulesMet && !isInsideSrnaTag && !matchesAlreadyAutoCorrectedCorrectedPhrase) {
        if (boundaryIndex != null) {
          boundaryIndex = remapBoundaryAfterReplacement(
            boundaryIndex,
            index,
            currentPhrase.length,
            correctedWord.length
          );
        }

        autoCorrectedText =
          autoCorrectedText.substr(0, index) +
          correctedWord +
          autoCorrectedText.substr(index + currentPhrase.length);
        correctedPhrases.push(correctedWord);
      }
      startIndex = index + correctedWord.length;
    }
  });

  if (autoCorrectedText !== initialText) {
    logger.info(
      `[generateAutoCorrectedText] AutoCorrected text from "${initialText}" to "${autoCorrectedText}"`
    );
  }

  return { text: autoCorrectedText, boundaryIndex };
};

const generateAutoCorrectedText = (
  autoCorrectedText: string,
  _dictionary: {
    [key: string]: string;
  }
) => runAutoCorrect(autoCorrectedText, _dictionary).text;

/**
 * Given a string of text, performs any autoCorrections from the user-defined autoCorrect Dictionary
 */
export const autoCorrectStringText = (text: string, dictionary: Record<string, string>): string => {
  if (Object.keys(dictionary).length === 0) {
    return text;
  }

  return generateAutoCorrectedText(text, dictionary);
};

export const getLongestAutoCorrectKeyLength = (
  autoCorrectDictionary: Record<string, string>
): number =>
  Object.keys(autoCorrectDictionary).reduce(
    (currentLongest, key) => Math.max(currentLongest, key.length),
    0
  );

export const getAutoCorrectedSeamText = ({
  beforeText,
  incomingText,
  seamLength,
  autoCorrectDictionary,
}: {
  beforeText: string;
  incomingText: string;
  seamLength: number;
  autoCorrectDictionary: Record<string, string>;
}): { nextBeforeText: string; nextIncomingText: string } => {
  const normalizeHyphenBoundarySpacing = (value: {
    nextBeforeText: string;
    nextIncomingText: string;
  }) => {
    let nextBeforeText = value.nextBeforeText;
    let nextIncomingText = value.nextIncomingText;

    // For hyphenated auto-correct outcomes, normalize seam spacing to:
    // "...word-joined" instead of "...word -joined" or "...word  -joined".
    if (/^\s+-/.test(nextIncomingText)) {
      nextIncomingText = nextIncomingText.replace(/^\s+/, '');
    }

    if (/\s$/.test(nextBeforeText) && nextIncomingText.startsWith('-')) {
      nextBeforeText = nextBeforeText.replace(/\s+$/, '');
    }

    return { nextBeforeText, nextIncomingText };
  };

  const effectiveSeamLength = Math.min(Math.max(0, seamLength), beforeText.length);
  const beforeTextPrefix = beforeText.slice(0, -effectiveSeamLength);
  const beforeTextSeam = beforeText.slice(-effectiveSeamLength);
  const attemptSeamAutoCorrect = (value: { stitchedText: string; boundaryIndex: number }) => {
    const {
      text: autoCorrectedStitchedText,
      boundaryIndex: correctedBoundaryIndexAcrossSeam = value.boundaryIndex,
    } = runAutoCorrect(value.stitchedText, autoCorrectDictionary, {
      boundaryIndex: value.boundaryIndex,
    });

    if (autoCorrectedStitchedText === value.stitchedText) {
      return null;
    }

    return normalizeHyphenBoundarySpacing({
      nextBeforeText: `${beforeTextPrefix}${autoCorrectedStitchedText.slice(
        0,
        correctedBoundaryIndexAcrossSeam
      )}`,
      nextIncomingText: autoCorrectedStitchedText.slice(correctedBoundaryIndexAcrossSeam),
    });
  };

  const directSeamAutoCorrect = attemptSeamAutoCorrect({
    stitchedText: `${beforeTextSeam}${incomingText}`,
    boundaryIndex: effectiveSeamLength,
  });
  if (directSeamAutoCorrect != null) {
    return directSeamAutoCorrect;
  }

  // Stable chunks can be trimmed upstream; when that happens, a phrase like
  // "simple appearing" may arrive as "...simple" + "appearing...".
  // Try a virtual boundary space to recover multi-word autoCorrect matches.
  const maybeNeedsBoundarySpace = /\S$/.test(beforeTextSeam) && /^\S/.test(incomingText);
  if (maybeNeedsBoundarySpace) {
    const boundarySpaceSeamAutoCorrect = attemptSeamAutoCorrect({
      stitchedText: `${beforeTextSeam} ${incomingText}`,
      boundaryIndex: effectiveSeamLength,
    });
    if (boundarySpaceSeamAutoCorrect != null) {
      return boundarySpaceSeamAutoCorrect;
    }
  }

  // If both chunk edges contain whitespace, normalized seam text has exactly one boundary space.
  // This helps dictionary phrases like "central appearing" match even when chunking produced
  // "...central " + " appearing...".
  const maybeNeedsCollapsedBoundaryWhitespace =
    /\s$/.test(beforeTextSeam) && /^\s/.test(incomingText);
  if (maybeNeedsCollapsedBoundaryWhitespace) {
    const normalizedBeforeSeam = beforeTextSeam.replace(/\s+$/, '');
    const normalizedIncomingText = incomingText.replace(/^\s+/, '');
    const collapsedBoundarySeamAutoCorrect = attemptSeamAutoCorrect({
      stitchedText: `${normalizedBeforeSeam} ${normalizedIncomingText}`,
      boundaryIndex: normalizedBeforeSeam.length,
    });
    if (collapsedBoundarySeamAutoCorrect != null) {
      return collapsedBoundarySeamAutoCorrect;
    }
  }

  return { nextBeforeText: beforeText, nextIncomingText: incomingText };
};

/**
 * Parses stable text and performs any autoCorrections from the user-defined autoCorrect Dictionary
 * Skips over substitutions
 */
export const autoCorrectNvoqStableText = (
  msg: NvoqMessage,
  _dictionary: {
    [key: string]: string;
  }
): NvoqMessage => {
  const autoCorrectedText = generateAutoCorrectedText(msg.data.text, _dictionary);

  return {
    ...msg,
    data: {
      ...msg.data,
      text: autoCorrectedText,
    },
  };
};
